import React, { useState } from "react";
import { createMerchant } from "../utils/api";
import { isSandboxHost } from "../utils/apiBase";
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Divider,
  Stack,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import IconButton from "@mui/material/IconButton";
import Loading from "./Loading";

const AddingMerchant = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [form, setForm] = useState<{
    merchant_name: string;
    username: string;
    fiat: boolean;
    callback_url_payin: string;
    callback_url_payout: string;
  }>({
    merchant_name: "",
    username: "",
    fiat: true,
    callback_url_payin: "",
    callback_url_payout: "",
  });
  const [userData, setUserData] = useState<{
    username: string;
    password: string;
    api_key: string;
  }>({ username: "", password: "", api_key: "" });
  const [copied, setCopied] = useState(false);

  // Контур определяется по домену админки: со стенда песочницы выдаются
  // доступы в песочный кабинет, с боевой — в боевой.
  const sandbox = isSandboxHost(window.location.hostname);
  const cabinetUrl = sandbox
    ? "https://sandbox-merchant.payfinzora.com/login"
    : "https://merchant.payfinzora.com/login";
  const baseUrl = sandbox
    ? "https://sandbox.payfinzora.com"
    : "https://api.payfinzora.com";

  /** Готовый блок для отправки мерчанту — размеченный под мессенджер. */
  const handoverText = [
    "——————————————————",
    ...(sandbox ? ["Sandbox"] : ["Prod"]),
    "Вход в лк: ",
    "",
    "```",
    cabinetUrl,
    "```",
    "",
    `login: \`${userData.username}\``,
    `password: \`${userData.password}\``,
    "",
    `api_key: \`${userData.api_key}\``,
    `BaseUrl: \`${baseUrl}\``,
    "",
    "——————————————————",
    "Документация доступна в лк",
  ].join("\n");

  const copyHandover = () => {
    navigator.clipboard.writeText(handoverText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const addMerchant = async () => {
    setLoading(true);
    try {
      const res = await createMerchant(form);
      setUserData({
        username: res.data.user.username,
        password: res.data.user.password,
        // Ключ отдаётся только здесь и только один раз: в базе лежит хеш.
        api_key: res.data.api_key,
      });
      setSuccess(true);
    } catch (err) {
      console.log(err);
      setError(String(err));
    } finally {
      setLoading(false);
    }
  };
  if (loading) return <Loading />;
  if (success)
    return (
      <Box
        sx={{
          textAlign: "center",
          width: "100%",
        }}
      >
        <CheckCircleRoundedIcon color="success" sx={{ fontSize: 72, mb: 2 }} />

        <Typography variant="h5" fontWeight={700} gutterBottom>
          Мерчант успешно создан
        </Typography>

        <Typography mb={3}>
          Сохраните эти данные. Пароль больше не будет показан.
        </Typography>

        <Stack spacing={2}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box textAlign="left">
              <Typography variant="caption">Username</Typography>

              <Typography fontWeight={600}>{userData.username}</Typography>
            </Box>

            <IconButton
              onClick={() => navigator.clipboard.writeText(userData.username)}
            >
              <ContentCopyRoundedIcon />
            </IconButton>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box textAlign="left">
              <Typography variant="caption">Password</Typography>

              <Typography
                fontWeight={600}
                sx={{
                  fontFamily: "monospace",
                }}
              >
                {userData.password}
              </Typography>
            </Box>

            <IconButton
              onClick={() => navigator.clipboard.writeText(userData.password)}
            >
              <ContentCopyRoundedIcon />
            </IconButton>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box textAlign="left" sx={{ minWidth: 0 }}>
              <Typography variant="caption">API key</Typography>

              <Typography
                fontWeight={600}
                sx={{
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                }}
              >
                {userData.api_key}
              </Typography>
            </Box>

            <IconButton
              onClick={() => navigator.clipboard.writeText(userData.api_key)}
            >
              <ContentCopyRoundedIcon />
            </IconButton>
          </Paper>
        </Stack>

        <Button
          fullWidth
          variant="contained"
          color={copied ? "success" : "primary"}
          startIcon={<ContentCopyRoundedIcon />}
          onClick={copyHandover}
          sx={{ mt: 2 }}
        >
          {copied
            ? "Скопировано"
            : `Скопировать блок для отправки${sandbox ? " (Sandbox)" : ""}`}
        </Button>

        <Divider sx={{ my: 3 }} />

        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            setSuccess(false);
            setUserData({ username: "", password: "", api_key: "" });
            setForm({
              merchant_name: "",
              username: "",
              fiat: true,
              callback_url_payin: "",
              callback_url_payout: "",
            });
          }}
        >
          Создать еще одного мерчанта
        </Button>
      </Box>
    );
  return (
    <Box
      margin="0 auto"
      width="98%"
      display="flex"
      gap="15px"
      flexDirection={"column"}
    >
      <TextField
        id="merchant_name"
        label="Название мерчанта"
        slotProps={{
          htmlInput: {
            step: "any",
          },
        }}
        value={form.merchant_name}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            merchant_name: e.target.value,
          }))
        }
        type="text"
        sx={{
          width: "100%",
          "& .MuiOutlinedInput-root": {
            height: "68px",
            alignItems: "center",
          },
          "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
            top: 6,
          },
        }}
      />
      <TextField
        id="username"
        label="Username"
        value={form.username}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            username: e.target.value,
          }))
        }
        slotProps={{
          htmlInput: {
            step: "any",
          },
        }}
        type="text"
        sx={{
          width: "100%",
          "& .MuiOutlinedInput-root": {
            height: "68px",
            alignItems: "center",
          },
          "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
            top: 6,
          },
        }}
      />
      <FormControl sx={{ width: "100%" }} fullWidth>
        <InputLabel
          sx={{
            "&.MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
              top: 6,
            },
          }}
        >
          Тип баланса
        </InputLabel>
        <Select
          value={form.fiat}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              fiat: e.target.value as boolean,
            }))
          }
          MenuProps={{
            disableScrollLock: true,
          }}
          sx={{ height: "68px" }}
          label="Тип баланса"
        >
          <MenuItem value={true as any}>
            <Typography>Фиатный</Typography>
          </MenuItem>

          <MenuItem value={false as any}>
            <Typography>Тезерный</Typography>
          </MenuItem>
        </Select>{" "}
      </FormControl>
      <TextField
        id="callback_url_payin"
        label="Payin callback url"
        value={form.callback_url_payin}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            callback_url_payin: e.target.value,
          }))
        }
        slotProps={{
          htmlInput: {
            step: "any",
          },
        }}
        type="text"
        sx={{
          width: "100%",
          "& .MuiOutlinedInput-root": {
            height: "68px",
            alignItems: "center",
          },
          "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
            top: 6,
          },
        }}
      />
      <TextField
        id="callback_url_payout"
        label="Payout callback url"
        value={form.callback_url_payout}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            callback_url_payout: e.target.value,
          }))
        }
        slotProps={{
          htmlInput: {
            step: "any",
          },
        }}
        type="text"
        sx={{
          width: "100%",
          "& .MuiOutlinedInput-root": {
            height: "68px",
            alignItems: "center",
          },
          "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
            top: 6,
          },
        }}
      />

      <Button
        variant="contained"
        sx={{
          width: "100%",
        }}
      >
        Отмена
      </Button>
      <Button
        disabled={
          !form.merchant_name ||
          !form.username ||
          !form.callback_url_payin ||
          !form.callback_url_payout
        }
        onClick={addMerchant}
        variant="contained"
      >
        Создать
      </Button>
    </Box>
  );
};

export default AddingMerchant;
